/**
 * OR-Sync Logic Engine
 * Parses intra-op dictation evidence and maps against standard preference cards.
 * Produces discrepancy analysis for the Battle View dashboard.
 */

export interface SupplyItem {
  id: string;
  name: string;
  category: 'implant' | 'suture' | 'instrument' | 'consumable' | 'graft' | 'cement';
  size?: string;
  quantity: number;
  catalogNumber?: string;
}

export interface PreferenceCard {
  id: string;
  surgeonId: string;
  surgeonName: string;
  cptCode: string;
  procedureName: string;
  items: SupplyItem[];
  lastUpdated: string;
}

export interface DictationExtraction {
  rawText: string;
  extractedItems: SupplyItem[];
  timestamp: string;
}

export type DiscrepancyType = 'waste' | 'adhoc' | 'matched';

export interface DiscrepancyItem {
  id: string;
  type: DiscrepancyType;
  cardItem?: SupplyItem;
  dictationItem?: SupplyItem;
  matchConfidence?: number;
  description: string;
}

export interface ORSyncResult {
  cardItems: SupplyItem[];
  dictationItems: SupplyItem[];
  discrepancies: DiscrepancyItem[];
  matchedCount: number;
  wasteCount: number;
  adhocCount: number;
  matchRate: number;
}

// Known clinical supply terms mapped to categories
const SUPPLY_PATTERNS: Array<{ pattern: RegExp; category: SupplyItem['category']; nameExtractor: (m: RegExpMatchArray) => string; sizeExtractor?: (m: RegExpMatchArray) => string }> = [
  { pattern: /size\s+(\d+)\s+(tapered|standard|cemented)?\s*stem/gi, category: 'implant', nameExtractor: (m) => `${m[2] || 'Standard'} Stem`, sizeExtractor: (m) => `Size ${m[1]}` },
  { pattern: /(\d+mm)\s+(acetabular|femoral)\s+(cup|head|liner|component)/gi, category: 'implant', nameExtractor: (m) => `${m[2]} ${m[3]}`, sizeExtractor: (m) => m[1] },
  { pattern: /(\d-\d)\s+(vicryl|monocryl|prolene|ethibond|pds)/gi, category: 'suture', nameExtractor: (m) => m[2], sizeExtractor: (m) => m[1] },
  { pattern: /(simplex|palacos|smartset)\s*(cement)?/gi, category: 'cement', nameExtractor: (m) => `${m[1]} Bone Cement` },
  { pattern: /(pulse\s*lavage|irrigation\s*system)/gi, category: 'instrument', nameExtractor: () => 'Pulse Lavage System' },
  { pattern: /(hemovac|drain|jackson-pratt|jp)\s*(drain)?/gi, category: 'consumable', nameExtractor: () => 'Closed Suction Drain' },
  { pattern: /(bovie|electrocautery|cautery)/gi, category: 'instrument', nameExtractor: () => 'Electrocautery Unit' },
  { pattern: /(allograft|autograft|bone\s*graft)/gi, category: 'graft', nameExtractor: (m) => m[0].trim() },
  { pattern: /polyethylene\s*(liner|insert|bearing)/gi, category: 'implant', nameExtractor: (m) => `Polyethylene ${m[1]}` },
  { pattern: /(trial|sizing)\s*(components?|implants?)/gi, category: 'instrument', nameExtractor: () => 'Trial Components Set' },
];

let idCounter = 0;
function genId(): string {
  return `supply-${++idCounter}-${Date.now()}`;
}

export function extractSuppliesFromDictation(transcript: string): SupplyItem[] {
  const items: SupplyItem[] = [];
  const seen = new Set<string>();

  for (const { pattern, category, nameExtractor, sizeExtractor } of SUPPLY_PATTERNS) {
    const regex = new RegExp(pattern.source, pattern.flags);
    let match: RegExpExecArray | null;
    while ((match = regex.exec(transcript)) !== null) {
      const name = nameExtractor(match);
      const key = `${name}-${sizeExtractor?.(match) || ''}`.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        items.push({
          id: genId(),
          name,
          category,
          size: sizeExtractor?.(match),
          quantity: 1,
        });
      }
    }
  }

  return items;
}

export function compareCardToDictation(card: SupplyItem[], dictation: SupplyItem[]): ORSyncResult {
  const discrepancies: DiscrepancyItem[] = [];
  const matchedCardIds = new Set<string>();
  const matchedDictIds = new Set<string>();

  // Match card items to dictation items
  for (const ci of card) {
    let bestMatch: SupplyItem | null = null;
    let bestConfidence = 0;

    for (const di of dictation) {
      if (matchedDictIds.has(di.id)) continue;
      const confidence = computeMatchConfidence(ci, di);
      if (confidence > bestConfidence && confidence >= 0.5) {
        bestMatch = di;
        bestConfidence = confidence;
      }
    }

    if (bestMatch) {
      matchedCardIds.add(ci.id);
      matchedDictIds.add(bestMatch.id);
      discrepancies.push({
        id: genId(),
        type: 'matched',
        cardItem: ci,
        dictationItem: bestMatch,
        matchConfidence: bestConfidence,
        description: `${ci.name} confirmed in dictation`,
      });
    } else {
      discrepancies.push({
        id: genId(),
        type: 'waste',
        cardItem: ci,
        description: `${ci.name}${ci.size ? ` (${ci.size})` : ''} — on card but NOT confirmed in dictation`,
      });
    }
  }

  // Find ad-hoc items (in dictation but not on card)
  for (const di of dictation) {
    if (!matchedDictIds.has(di.id)) {
      discrepancies.push({
        id: genId(),
        type: 'adhoc',
        dictationItem: di,
        description: `${di.name}${di.size ? ` (${di.size})` : ''} — dictated but NOT on preference card`,
      });
    }
  }

  const matchedCount = discrepancies.filter(d => d.type === 'matched').length;
  const wasteCount = discrepancies.filter(d => d.type === 'waste').length;
  const adhocCount = discrepancies.filter(d => d.type === 'adhoc').length;
  const total = card.length + dictation.length - matchedCount;

  return {
    cardItems: card,
    dictationItems: dictation,
    discrepancies,
    matchedCount,
    wasteCount,
    adhocCount,
    matchRate: total > 0 ? Math.round((matchedCount / total) * 100) : 0,
  };
}

function computeMatchConfidence(a: SupplyItem, b: SupplyItem): number {
  let score = 0;
  if (a.category === b.category) score += 0.3;
  const aN = a.name.toLowerCase();
  const bN = b.name.toLowerCase();
  if (aN === bN) score += 0.5;
  else if (aN.includes(bN) || bN.includes(aN)) score += 0.35;
  if (a.size && b.size && a.size.toLowerCase() === b.size.toLowerCase()) score += 0.2;
  return Math.min(score, 1);
}
